import { memo, useEffect, useState, type KeyboardEvent, type MouseEvent } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Link2, Plus } from "lucide-react";
import { Badge, Popover, PopoverClose } from "../../ui/index.js";
import type { InsertKind } from "../editor/block-templates.js";

export type EpisodeFlowNodeKind = "block" | "choice" | "option" | "branch" | "route" | "target" | "insert" | "lane";
export type EpisodeRouteResult = "success" | "fail" | "any";

export interface EpisodeRouteQuickTarget {
  readonly kind: "next" | "end";
  readonly value: string;
  readonly label: string;
  readonly group: "episode" | "ending";
}

export interface EpisodeRouteQuickResult {
  readonly value: EpisodeRouteResult;
  readonly label: string;
}

export interface EpisodeFlowStep {
  readonly kind: string;
  readonly text: string;
  readonly tone: string;
  readonly concurrent?: boolean;
}

export interface EpisodeFlowInsertOption {
  readonly kind: InsertKind;
  readonly label: string;
  readonly token: string;
  readonly disabledReason?: string;
  readonly subOptions?: readonly EpisodeFlowInsertOption[];
}

export interface EpisodeFlowNodeData {
  readonly nodeKind: EpisodeFlowNodeKind;
  readonly title: string;
  readonly label?: string;
  readonly summary?: string;
  readonly range?: string;
  readonly tone: string;
  readonly line?: number;
  readonly selected?: boolean;
  readonly steps?: readonly EpisodeFlowStep[];
  readonly badges?: readonly string[];
  readonly impacts?: readonly string[];
  readonly muted?: string;
  readonly icon?: string;
  readonly routeTargets?: readonly EpisodeRouteQuickTarget[];
  readonly routeResults?: readonly EpisodeRouteQuickResult[];
  readonly quickConnectLabel?: string;
  readonly quickConnectTitle?: string;
  readonly quickConnectEpisodesLabel?: string;
  readonly quickConnectEndingsLabel?: string;
  readonly missing?: boolean;
  readonly missingLabel?: string;
  readonly createMissingLabel?: string;
  readonly missingTarget?: string;
  readonly onCreateMissing?: (target: string) => void;
  readonly onQuickConnect?: (target: EpisodeRouteQuickTarget, result?: EpisodeRouteResult) => void;
  readonly insertOptions?: readonly EpisodeFlowInsertOption[];
  readonly onInsert?: (kind: InsertKind) => void;
  readonly onSelectLine?: (line: number) => void;
  readonly onDeleteLine?: (line: number) => void;
  readonly dropPlacement?: "before" | "after";
  readonly dropActive?: boolean;
  readonly sidecar?: boolean;
  readonly sequenceConnectable?: boolean;
  readonly sourceConnectable?: boolean;
  readonly targetConnectable?: boolean;
  readonly optionId?: string;
  readonly optionLine?: number;
  readonly laneHeight?: number;
  readonly laneIndex?: number;
  readonly branchInsertLabel?: string;
  [key: string]: unknown;
}

function useIsSmallViewport(): boolean {
  const [isSmall, setIsSmall] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(max-width: 640px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(max-width: 640px)");
    const update = () => setIsSmall(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return isSmall;
}

function groupedSteps(steps: readonly EpisodeFlowStep[]): readonly (EpisodeFlowStep | readonly EpisodeFlowStep[])[] {
  const groups: (EpisodeFlowStep | EpisodeFlowStep[])[] = [];

  for (const step of steps) {
    if (step.concurrent) {
      const previous = groups[groups.length - 1];
      if (Array.isArray(previous)) {
        previous.push(step);
      } else if (previous) {
        groups[groups.length - 1] = [previous, step];
      } else {
        groups.push([step]);
      }
      continue;
    }
    groups.push(step);
  }
  return groups;
}

function isStepGroup(item: EpisodeFlowStep | readonly EpisodeFlowStep[]): item is readonly EpisodeFlowStep[] {
  return Array.isArray(item);
}

function isEditableTarget(target: EventTarget | null, currentTarget: Element): boolean {
  if (!(target instanceof Element) || target === currentTarget) return false;
  const editable = target.closest("[contenteditable]");
  return Boolean(
    target.closest("button,input,textarea,select,[role='textbox']") ||
      (editable instanceof HTMLElement && editable.isContentEditable),
  );
}

function StepPill({ step, index }: { readonly step: EpisodeFlowStep; readonly index: number }): JSX.Element {
  return (
    <span key={`${index}:${step.text}`} className="lc-episode-flow-node__step" style={{ ["--lc-step-kind" as string]: step.tone }}>
      <span className="lc-episode-flow-node__step-kind">{step.kind}</span>
      <span className="lc-episode-flow-node__step-text">{step.text}</span>
    </span>
  );
}

function EpisodeFlowNodeImpl({ data }: NodeProps) {
  const d = data as EpisodeFlowNodeData;
  const isSmallViewport = useIsSmallViewport();
  const [insertPanel, setInsertPanel] = useState<string | null>(null);
  const activeBranchInsertOption = d.nodeKind === "branch" ? d.insertOptions?.find((option) => option.kind === insertPanel) : undefined;
  const visibleBranchInsertOptions = activeBranchInsertOption?.subOptions ?? d.insertOptions ?? [];
  if (d.nodeKind === "insert") {
    const activeInsertOption = d.insertOptions?.find((option) => option.kind === insertPanel);
    const visibleInsertOptions = activeInsertOption?.subOptions ?? d.insertOptions ?? [];
    return (
      <div className="lc-episode-flow-insert nodrag nopan" data-clickable="true" data-drop-active={d.dropActive || undefined}>
        <Handle
          id="target-top"
          type="target"
          position={Position.Top}
          className="lc-canvas-handle lc-canvas-handle--insert"
          isConnectable={false}
        />
        <Popover
          side={isSmallViewport ? "bottom" : "right"}
          align="center"
          sideOffset={10}
          collisionPadding={14}
          className="lc-flow-insert-menu"
          onOpenChange={(open) => {
            if (!open) setInsertPanel(null);
          }}
          trigger={
            <button type="button" className="lc-episode-flow-insert__button">
              <Plus size={12} aria-hidden="true" />
              <span>{d.title}</span>
            </button>
          }
        >
          {activeInsertOption ? (
            <button type="button" className="lc-flow-insert-menu__back" onClick={() => setInsertPanel(null)}>
              ← {activeInsertOption.label}
            </button>
          ) : null}
          <div className="lc-flow-insert-menu__grid" role="list" aria-label={activeInsertOption?.label ?? d.title}>
            {visibleInsertOptions.map((option) => {
              const item = (
                <button
                  key={option.kind}
                  type="button"
                  className="lc-flow-insert-menu__item"
                  disabled={Boolean(option.disabledReason)}
                  title={option.disabledReason ?? option.label}
                  style={{ ["--lc-kind" as string]: `var(${option.token})` }}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (option.subOptions?.length) setInsertPanel(option.kind);
                    else d.onInsert?.(option.kind);
                  }}
                >
                  <span className="lc-flow-insert-menu__swatch" aria-hidden="true" />
                  <span>{option.label}</span>
                </button>
              );
              return option.subOptions?.length ? item : <PopoverClose asChild key={option.kind}>{item}</PopoverClose>;
            })}
          </div>
        </Popover>
        <Handle
          id="source-bottom"
          type="source"
          position={Position.Bottom}
          className="lc-canvas-handle lc-canvas-handle--insert"
          isConnectable={false}
        />
      </div>
    );
  }

  if (d.nodeKind === "lane") {
    return (
      <div
        className="lc-episode-flow-lane"
        data-lane-index={d.laneIndex}
        style={{
          ["--lc-kind" as string]: d.tone,
          ["--lc-lane-height" as string]: `${d.laneHeight ?? 360}px`,
        }}
        aria-hidden="true"
      >
        <span className="lc-episode-flow-lane__label">
          {d.icon ? <span>{d.icon}</span> : null}
          {d.title}
        </span>
      </div>
    );
  }

  const selectable = typeof d.line === "number" && Boolean(d.onSelectLine);
  const sequenceConnectable = d.sequenceConnectable === true;
  const sourceConnectable = d.sourceConnectable ?? (d.nodeKind === "option" || sequenceConnectable);
  const targetConnectable = d.targetConnectable ?? (d.nodeKind === "target" || sequenceConnectable);
  const targetHandleClass = `lc-canvas-handle${targetConnectable ? " lc-canvas-handle--connect-target" : ""}`;
  const sourceHandleClass = `lc-canvas-handle${sourceConnectable ? " lc-canvas-handle--connect-source" : ""}`;
  const select = () => {
    if (selectable) d.onSelectLine?.(d.line!);
  };
  const createMissing = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (d.missingTarget) d.onCreateMissing?.(d.missingTarget);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!selectable) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      select();
      return;
    }
    if ((event.key === "Delete" || event.key === "Backspace") && typeof d.line === "number" && d.onDeleteLine) {
      if (isEditableTarget(event.target, event.currentTarget)) return;
      event.preventDefault();
      event.stopPropagation();
      d.onDeleteLine(d.line);
    }
  };

  return (
    <div
      className={`lc-episode-flow-node lc-episode-flow-node--${d.nodeKind}`}
      data-connectable={sourceConnectable || targetConnectable || undefined}
      data-sequence-connectable={sequenceConnectable || undefined}
      data-route-outcome={typeof d.routeOutcomeKind === "string" ? d.routeOutcomeKind : undefined}
      data-missing={d.missing || undefined}
      data-selected={d.selected || undefined}
      data-reorderable={d.reorderable === true || undefined}
      data-clickable={selectable || undefined}
      data-drop-placement={d.dropPlacement}
      data-sidecar={d.sidecar || undefined}
      role={selectable ? "button" : undefined}
      tabIndex={selectable ? 0 : undefined}
      aria-keyshortcuts={selectable && d.onDeleteLine ? "Delete Backspace" : undefined}
      onClick={selectable ? select : undefined}
      onKeyDown={onKeyDown}
      style={{ ["--lc-kind" as string]: d.tone }}
    >
      {d.dropPlacement ? <span className="lc-episode-flow-node__drop-marker" aria-hidden="true" /> : null}
      <Handle id="target-top" type="target" position={Position.Top} className={targetHandleClass} isConnectable={targetConnectable} />

      <header className="lc-episode-flow-node__head">
        {d.icon ? <span className="lc-episode-flow-node__icon" aria-hidden="true">{d.icon}</span> : null}
        <div className="lc-episode-flow-node__title-group">
          {d.label ? <span className="lc-episode-flow-node__label">{d.label}</span> : null}
          <h3 className="lc-episode-flow-node__title">{d.title}</h3>
        </div>
        {d.range ? <span className="lc-episode-flow-node__range">{d.range}</span> : null}
      </header>

      {d.summary ? <p className="lc-episode-flow-node__summary">{d.summary}</p> : null}

      {d.badges?.length ? (
        <div className="lc-episode-flow-node__badges">
          {d.badges.map((badge) => (
            <Badge key={badge} size="sm" variant="tonal" tone="neutral">
              {badge}
            </Badge>
          ))}
        </div>
      ) : null}

      {d.steps?.length ? (
        <div className="lc-episode-flow-node__steps" aria-label="steps">
          {groupedSteps(d.steps).map((item, groupIndex) => {
            if (!isStepGroup(item)) return <StepPill key={`${groupIndex}:${item.text}`} step={item} index={groupIndex} />;
            return (
              <div key={`concurrent:${groupIndex}`} className="lc-episode-flow-node__step-group" data-concurrent="true">
                <span className="lc-episode-flow-node__step-chip">Together</span>
                <div className="lc-episode-flow-node__step-group-items">
                  {item.map((step, index) => <StepPill key={`${index}:${step.text}`} step={step} index={index} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {d.impacts?.length ? (
        <div className="lc-episode-flow-node__impacts">
          {d.impacts.map((impact) => (
            <span key={impact} className="lc-episode-flow-node__impact">{impact}</span>
          ))}
        </div>
      ) : null}

      {d.muted ? <span className="lc-episode-flow-node__muted">{d.muted}</span> : null}

      {d.nodeKind === "branch" && d.insertOptions?.length && d.onInsert ? (
        <Popover
          side={isSmallViewport ? "bottom" : "right"}
          align="start"
          className="lc-flow-insert-menu lc-flow-insert-menu--branch"
          onOpenChange={(open) => {
            if (!open) setInsertPanel(null);
          }}
          trigger={
            <button
              type="button"
              className="lc-episode-flow-node__branch-add nodrag nopan"
              onClick={(event) => event.stopPropagation()}
            >
              <Plus size={13} aria-hidden="true" />
              {d.branchInsertLabel ?? d.title}
            </button>
          }
        >
          {activeBranchInsertOption ? (
            <button type="button" className="lc-flow-insert-menu__back" onClick={() => setInsertPanel(null)}>
              ← {activeBranchInsertOption.label}
            </button>
          ) : null}
          <div className="lc-flow-insert-menu__grid" role="list" aria-label={activeBranchInsertOption?.label ?? d.branchInsertLabel ?? d.title}>
            {visibleBranchInsertOptions.map((option) => {
              const item = (
                <button
                  key={option.kind}
                  type="button"
                  className="lc-flow-insert-menu__item"
                  disabled={Boolean(option.disabledReason)}
                  title={option.disabledReason ?? option.label}
                  style={{ ["--lc-kind" as string]: `var(${option.token})` }}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (option.subOptions?.length) setInsertPanel(option.kind);
                    else d.onInsert?.(option.kind);
                  }}
                >
                  <span className="lc-flow-insert-menu__swatch" aria-hidden="true" />
                  <span>{option.label}</span>
                </button>
              );
              return option.subOptions?.length ? item : <PopoverClose asChild key={option.kind}>{item}</PopoverClose>;
            })}
          </div>
        </Popover>
      ) : null}

      {d.missing ? (
        <div className="lc-episode-flow-node__missing">
          <Badge size="sm" variant="tonal" tone="danger">
            {d.missingLabel}
          </Badge>
          {d.missingTarget && d.onCreateMissing ? (
            <button type="button" className="lc-episode-flow-node__mini-action nodrag nopan" onClick={createMissing}>
              <Plus size={13} aria-hidden="true" />
              {d.createMissingLabel}
            </button>
          ) : null}
        </div>
      ) : null}

      {d.nodeKind === "option" && d.routeTargets?.length && d.onQuickConnect ? (
        <Popover
          side="right"
          align="start"
          className="lc-route-connect"
          trigger={
            <button
              type="button"
              className="lc-episode-flow-node__connect nodrag nopan"
              onClick={(event) => event.stopPropagation()}
            >
              <Link2 size={13} aria-hidden="true" />
              {d.quickConnectLabel}
            </button>
          }
        >
          <div className="lc-route-connect__panel" onClick={(event) => event.stopPropagation()}>
            {d.quickConnectTitle ? <p className="lc-route-connect__title">{d.quickConnectTitle}</p> : null}
            {d.routeResults?.length && d.routeResults.length > 1 ? (
              <div className="lc-route-connect__results" role="list">
                {d.routeResults.map((result) => (
                  <section key={result.value} className="lc-route-connect__result" aria-label={result.label}>
                    <span className="lc-route-connect__result-label">{result.label}</span>
                    <RouteTargetButtons data={d} result={result.value} />
                  </section>
                ))}
              </div>
            ) : (
              <RouteTargetButtons data={d} result={d.routeResults?.[0]?.value} />
            )}
          </div>
        </Popover>
      ) : null}

      <Handle id="source-bottom" type="source" position={Position.Bottom} className={sourceHandleClass} isConnectable={sourceConnectable} />
    </div>
  );
}

function RouteTargetButtons({
  data,
  result,
}: {
  data: EpisodeFlowNodeData;
  result?: EpisodeRouteResult;
}) {
  const episodes = data.routeTargets?.filter((target) => target.group === "episode") ?? [];
  const endings = data.routeTargets?.filter((target) => target.group === "ending") ?? [];
  return (
    <div className="lc-route-connect__groups">
      {episodes.length > 0 ? (
        <section className="lc-route-connect__group" aria-label={data.quickConnectEpisodesLabel}>
          <span className="lc-route-connect__group-label">{data.quickConnectEpisodesLabel}</span>
          <div className="lc-route-connect__items">
            {episodes.map((target) => (
              <PopoverClose asChild key={`${result ?? "any"}:${target.kind}:${target.value}`}>
                <button
                  type="button"
                  className="lc-route-connect__item nodrag nopan"
                  onClick={(event) => {
                    event.stopPropagation();
                    data.onQuickConnect?.(target, result);
                  }}
                >
                  {target.label}
                </button>
              </PopoverClose>
            ))}
          </div>
        </section>
      ) : null}
      {endings.length > 0 ? (
        <section className="lc-route-connect__group" aria-label={data.quickConnectEndingsLabel}>
          <span className="lc-route-connect__group-label">{data.quickConnectEndingsLabel}</span>
          <div className="lc-route-connect__items">
            {endings.map((target) => (
              <PopoverClose asChild key={`${result ?? "any"}:${target.kind}:${target.value}`}>
                <button
                  type="button"
                  className="lc-route-connect__item lc-route-connect__item--ending nodrag nopan"
                  onClick={(event) => {
                    event.stopPropagation();
                    data.onQuickConnect?.(target, result);
                  }}
                >
                  {target.label}
                </button>
              </PopoverClose>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

export const EpisodeFlowNode = memo(EpisodeFlowNodeImpl);
